import React, { useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Plus, X } from 'lucide-react';

const PollsTab = ({ polls, newPoll, setNewPoll, fetchPolls, handleCreatePoll, handleTogglePoll }) => {
  useEffect(() => {
    fetchPolls();
  }, []);

  return (
    <div className="space-y-6">
      {/* Create New Poll */}
      <Card>
        <CardHeader>
          <CardTitle>Create New Poll</CardTitle>
          <CardDescription>
            Create a new poll to display on the dashboard. Only one poll can be active at a time.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Question</label>
            <Input
              value={newPoll.question}
              onChange={(e) => setNewPoll({...newPoll, question: e.target.value})}
              placeholder="What's your wellness question?"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Options</label>
            <div className="space-y-2">
              {newPoll.options.map((option, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={option}
                    onChange={(e) => {
                      const newOptions = [...newPoll.options];
                      newOptions[index] = e.target.value;
                      setNewPoll({...newPoll, options: newOptions});
                    }}
                    placeholder={`Option ${index + 1}`}
                  />
                  {newPoll.options.length > 2 && (
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => {
                        const newOptions = newPoll.options.filter((_, i) => i !== index);
                        setNewPoll({...newPoll, options: newOptions});
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setNewPoll({...newPoll, options: [...newPoll.options, '']})}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Option
              </Button>
            </div>
          </div>

          <Button onClick={handleCreatePoll} className="w-full">
            Create Poll
          </Button>
        </CardContent>
      </Card>

      {/* Existing Polls */}
      <Card>
        <CardHeader>
          <CardTitle>All Polls</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {polls.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No polls created yet</p>
            ) : (
              polls.map((poll) => (
                <Card key={poll.id} className="border">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <CardTitle className="text-lg">{poll.question}</CardTitle>
                        <CardDescription>
                          Created {new Date(poll.created_at).toLocaleDateString()} • {poll.total_votes} votes
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Badge variant={poll.is_active ? "default" : "secondary"}>
                          {poll.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleTogglePoll(poll.id)}
                        >
                          {poll.is_active ? 'Deactivate' : 'Activate'}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {poll.options.map((option, index) => {
                        const votes = poll.vote_counts?.[index] || 0;
                        const percentage = poll.total_votes > 0 
                          ? Math.round((votes / poll.total_votes) * 100)
                          : 0;
                        
                        return (
                          <div key={index} className="flex items-center gap-3">
                            <div className="flex-1 bg-gray-100 rounded-lg p-3 relative overflow-hidden">
                              <div 
                                className="absolute inset-0 bg-burgundy/20 transition-all"
                                style={{ width: `${percentage}%` }}
                              />
                              <div className="relative flex justify-between items-center">
                                <span className="font-medium">{option}</span>
                                <span className="text-sm text-gray-600">
                                  {votes} ({percentage}%)
                                </span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export { PollsTab };
